import { NgFor } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CUSTOM_DATE_FORMATS } from '@app/core/shared/helpers/date-time.util';
import { ClarityService } from '@services/clarity.service';
import { CustomDateAdapter } from '../../adapters/custom-date-adapter';
import { FieldViewControlService } from '../../services/field-view-control.service';
import { OpenWeatherBaseMapLayerService } from '../../services/open-weather-base-map-layer.service';
import TimestampUtility from '../../utilities/timestamp.utility';

const materialDs = [
  MatFormFieldModule,
  MatSelectModule,
  MatIconModule,
  MatInputModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatTooltipModule
];

const common = [NgFor];

@Component({
  selector: 'app-weather-panel',
  templateUrl: './weather-panel.component.html',
  styleUrls: ['./weather-panel.component.scss'],
  imports: [...common, ...materialDs],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WeatherPanelComponent {
  private readonly fieldViewControlService = inject(FieldViewControlService);
  private readonly clarityService = inject(ClarityService);
  private readonly openWeatherBaseMapLayerService = inject(OpenWeatherBaseMapLayerService);

  readonly hours = Array.from({ length: 24 }, (_, i) => i);
  readonly minutes = [0, 15, 30, 45];

  constructor() {
    this.clarityService.initializeClarity();
  }

  readonly dateRestrictionFilter = (d: Date | null): boolean => {
    if (!d) return false;

    const minDate = new Date(2019, 2, 18); // March 18, 2019 is the limit of our free open weather trial
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 5); // 5 days from today is also the max limit for our free trial

    return d >= minDate && d <= maxDate;
  };

  readonly selectedWeatherBaseMapLayerSignal = this.fieldViewControlService.selectedWeatherBaseMapLayer;

  readonly weatherUnixTimeSignal = computed(() => {
    const unixTimestamp = this.fieldViewControlService.weatherUnixTime();
    const dateTimeNow = TimestampUtility.convertUnixTimestampToDate(unixTimestamp);
    return dateTimeNow;
  });

  readonly selectedDateSignal = computed(() => {
    const unixTimestamp = this.fieldViewControlService.weatherUnixTime();

    return TimestampUtility.convertUnixTimestampToDate(unixTimestamp);
  });

  readonly selectedHourSignal = computed(() => {
    const unixTimestamp = this.fieldViewControlService.weatherUnixTime();
    return TimestampUtility.convertUnixTimestampToDate(unixTimestamp).getHours();
  });

  readonly selectedMinutesSignal = computed(() => {
    const unixTimestamp = this.fieldViewControlService.weatherUnixTime();
    return TimestampUtility.convertUnixTimestampToDate(unixTimestamp).getMinutes();
  });

  readonly openWeatherBaseMapLayers = this.openWeatherBaseMapLayerService.openWeatherBaseMapLayers;

  updateSelectedWeatherBaseMapLayer(value: string): void {
    this.clarityService.clarityEvent(`field-view:weather-layer-select-${value}`);
    this.fieldViewControlService.setSelectedWeatherBaseMapLayer(value);
  }

  updateSelectedHour(hour: number): void {
    this.clarityService.clarityEvent('field-view:weather-hour-input');
    const currentDate = TimestampUtility.convertUnixTimestampToDate(
      this.fieldViewControlService.weatherUnixTime()
    );
    currentDate.setHours(hour);

    this.fieldViewControlService.setWeatherUnixTime(TimestampUtility.convertDateToUnixTimestamp(currentDate));
  }

  updateSelectedMinutes(minutes: number): void {
    this.clarityService.clarityEvent('field-view:weather-minutes-input');
    const currentDate = TimestampUtility.convertUnixTimestampToDate(
      this.fieldViewControlService.weatherUnixTime()
    );
    currentDate.setMinutes(minutes);

    this.fieldViewControlService.setWeatherUnixTime(TimestampUtility.convertDateToUnixTimestamp(currentDate));
  }

  updateSelectedDate(date: Date): void {
    const currentDate = TimestampUtility.convertUnixTimestampToDate(
      this.fieldViewControlService.weatherUnixTime()
    );
    currentDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());

    this.fieldViewControlService.setWeatherUnixTime(TimestampUtility.convertDateToUnixTimestamp(currentDate));
  }
}
